/**
 * Reports.
 *
 * Every figure comes from a database view rather than being assembled here, so
 * a printed report and the same report on screen can never disagree.
 *
 * The tax report is the one that matters most: it is what gets reconciled
 * against the authority's own portal at month end, and it deliberately shows
 * invoices that were never reported rather than quietly leaving them out.
 */

import { useState, useCallback } from 'react';
import {
    FiDownload, FiBarChart2, FiFileText, FiPackage, FiTrendingDown,
    FiUsers, FiPercent
} from 'react-icons/fi';

import { reportApi } from '../services/endpoints';
import useAsync from '../hooks/useAsync';
import DataTable from '../components/ui/DataTable';
import { PageHeader, StatCard, StatusBadge, Badge } from '../components/ui/Feedback';
import { money, quantity, formatDate, formatDateTime, toInputDate } from '../utils/format';

/** CSV export. Excel is where these numbers end up, so meet them there. */
const exportCsv = (rows, columns, filename) => {
    if (!rows?.length) return;

    const escape = (value) => {
        const text = value === null || value === undefined ? '' : String(value);
        return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };

    const header = columns.map((c) => escape(c.header)).join(',');
    const body = rows
        .map((row) => columns.map((c) => escape(c.csv ? c.csv(row) : row[c.key])).join(','))
        .join('\n');

    const blob = new Blob([`﻿${header}\n${body}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
};

const TABS = [
    { key: 'sales', label: 'Sales', icon: FiBarChart2 },
    { key: 'items', label: 'Items', icon: FiPackage },
    { key: 'tax', label: 'Sales tax', icon: FiPercent },
    { key: 'stock', label: 'Stock', icon: FiPackage },
    { key: 'wastage', label: 'Wastage', icon: FiTrendingDown },
    { key: 'staff', label: 'Staff', icon: FiUsers }
];

const ReportsPage = () => {
    const [tab, setTab] = useState('sales');
    const [range, setRange] = useState({
        fromDate: toInputDate(new Date(Date.now() - 6 * 86400000)),
        toDate: toInputDate(new Date())
    });

    const load = useCallback(() => {
        const fn = {
            sales: reportApi.sales,
            items: reportApi.items,
            tax: reportApi.tax,
            stock: reportApi.stock,
            wastage: reportApi.wastage,
            staff: reportApi.staff
        }[tab];
        return fn(range);
    }, [tab, range]);

    const { data, loading } = useAsync(load, [tab, range]);

    /* ------------------------------- columns ------------------------------ */

    const COLUMNS = {
        sales: [
            { key: 'Sales_Date', header: 'Date', render: (r) => formatDate(r.Sales_Date), csv: (r) => formatDate(r.Sales_Date) },
            { key: 'Order_Count', header: 'Orders', align: 'right' },
            { key: 'Gross_Sales', header: 'Gross', align: 'right', render: (r) => money(r.Gross_Sales), csv: (r) => r.Gross_Sales },
            { key: 'Total_Discount', header: 'Discount', align: 'right', render: (r) => money(r.Total_Discount), csv: (r) => r.Total_Discount },
            { key: 'Net_Sales', header: 'Net', align: 'right', render: (r) => money(r.Net_Sales), csv: (r) => r.Net_Sales },
            { key: 'Tax_Collected', header: 'Sales tax', align: 'right', render: (r) => money(r.Tax_Collected), csv: (r) => r.Tax_Collected },
            { key: 'Cash_Sales', header: 'Cash', align: 'right', render: (r) => money(r.Cash_Sales), csv: (r) => r.Cash_Sales },
            { key: 'Digital_Sales', header: 'Card / wallet', align: 'right', render: (r) => money(r.Digital_Sales), csv: (r) => r.Digital_Sales },
            { key: 'Total_Sales', header: 'Total', align: 'right', render: (r) => <span className="font-semibold">{money(r.Total_Sales)}</span>, csv: (r) => r.Total_Sales },
            { key: 'Average_Ticket', header: 'Average', align: 'right', render: (r) => money(r.Average_Ticket), csv: (r) => r.Average_Ticket }
        ],
        items: [
            { key: 'Item_Name', header: 'Item', render: (r) => (
                <div><p className="font-medium">{r.Item_Name}</p>
                    {r.Variant_Name && <p className="text-xs text-slate-500">{r.Variant_Name}</p>}</div>
            ), csv: (r) => `${r.Item_Name}${r.Variant_Name ? ` (${r.Variant_Name})` : ''}` },
            { key: 'Category_Name', header: 'Category' },
            { key: 'Quantity_Sold', header: 'Sold', align: 'right', render: (r) => quantity(r.Quantity_Sold), csv: (r) => r.Quantity_Sold },
            { key: 'Net_Amount', header: 'Net', align: 'right', render: (r) => money(r.Net_Amount), csv: (r) => r.Net_Amount },
            { key: 'Tax_Amount', header: 'Tax', align: 'right', render: (r) => money(r.Tax_Amount), csv: (r) => r.Tax_Amount },
            { key: 'Total_Amount', header: 'Total', align: 'right', render: (r) => <span className="font-semibold">{money(r.Total_Amount)}</span>, csv: (r) => r.Total_Amount },
            { key: 'Estimated_Cost', header: 'Cost', align: 'right', render: (r) => money(r.Estimated_Cost), csv: (r) => r.Estimated_Cost },
            { key: 'Estimated_Margin', header: 'Margin', align: 'right', render: (r) => (
                <span className={Number(r.Estimated_Margin) > 0 ? 'text-emerald-700' : 'text-red-600'}>
                    {money(r.Estimated_Margin)}
                </span>
            ), csv: (r) => r.Estimated_Margin }
        ],
        wastage: [
            { key: 'Adjustment_Date', header: 'Date', render: (r) => formatDate(r.Adjustment_Date), csv: (r) => formatDate(r.Adjustment_Date) },
            { key: 'Adjustment_Number', header: 'Reference' },
            { key: 'Stock_Item_Name', header: 'Item' },
            { key: 'Wastage_Reason_Name', header: 'Reason' },
            { key: 'Adjustment_Quantity', header: 'Quantity', align: 'right', render: (r) => quantity(Math.abs(r.Adjustment_Quantity)), csv: (r) => r.Adjustment_Quantity },
            { key: 'Cost_Impact', header: 'Cost', align: 'right', render: (r) => (
                <span className="font-medium text-red-600">{money(Math.abs(r.Cost_Impact))}</span>
            ), csv: (r) => r.Cost_Impact },
            { key: 'Recorded_By', header: 'Recorded by' }
        ],
        staff: [
            { key: 'Staff_Name', header: 'Staff', render: (r) => r.Staff_Name || '—' },
            { key: 'Order_Count', header: 'Orders', align: 'right' },
            { key: 'Total_Sales', header: 'Sales', align: 'right', render: (r) => money(r.Total_Sales), csv: (r) => r.Total_Sales },
            { key: 'Average_Ticket', header: 'Average', align: 'right', render: (r) => money(r.Average_Ticket), csv: (r) => r.Average_Ticket },
            { key: 'Total_Discount', header: 'Discounts', align: 'right', render: (r) => money(r.Total_Discount), csv: (r) => r.Total_Discount },
            { key: 'Void_Count', header: 'Voids', align: 'right', render: (r) => (
                // Voids by cashier is the number a manager is actually looking
                // for on this report.
                Number(r.Void_Count) > 0
                    ? <Badge tone="amber">{r.Void_Count}</Badge>
                    : <span className="text-slate-400">0</span>
            ), csv: (r) => r.Void_Count }
        ]
    };

    /* ------------------------------ rendering ----------------------------- */

    const renderTaxReport = () => {
        if (!data) return null;

        return (
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
                <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
                    {data.byRate.map((row) => (
                        <StatCard
                            key={row.Tax_Label}
                            label={`Output tax at ${row.Tax_Label}`}
                            value={`Rs. ${money(row.Tax_Amount)}`}
                            sub={`on Rs. ${money(row.Taxable_Amount)} · ${row.Invoice_Count} invoices`}
                            tone="brand"
                        />
                    ))}
                </div>

                {/* Anything not accepted by the authority is a gap in the return */}
                <section className="card p-4">
                    <h3 className="mb-2 font-semibold text-slate-800">Reporting status</h3>
                    <div className="flex flex-wrap gap-2">
                        {data.byStatus.map((row) => (
                            <div
                                key={row.Status_Code}
                                className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2"
                            >
                                <StatusBadge status={row.Status_Code} label={row.Status_Name} />
                                <span className="tabular text-sm text-slate-700">
                                    {row.Invoice_Count} · Rs. {money(row.Total_Amount)}
                                </span>
                            </div>
                        ))}
                    </div>
                </section>

                <div className="table-wrap">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Invoice</th>
                                <th>Authority number</th>
                                <th>Customer</th>
                                <th className="text-right">Total</th>
                                <th className="text-right">Tax</th>
                                <th>Paid by</th>
                                <th>Reported</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.invoices.map((row) => (
                                <tr key={row.Order_ID}>
                                    <td className="whitespace-nowrap">{formatDateTime(row.Order_Date_Time)}</td>
                                    <td>{row.Invoice_Number}</td>
                                    <td className="tabular text-xs">{row.Authority_Invoice_Number || '—'}</td>
                                    <td className="max-w-[10rem] truncate">
                                        {row.Customer_Name || 'Walk-in'}
                                        {(row.Customer_NTN || row.Customer_CNIC) && (
                                            <span className="block text-xs text-slate-500">
                                                {row.Customer_NTN || row.Customer_CNIC}
                                            </span>
                                        )}
                                    </td>
                                    <td className="tabular text-right">{money(row.Total_Payable)}</td>
                                    <td className="tabular text-right font-medium">{money(row.Tax_Amount)}</td>
                                    <td>{row.Payment_Class}</td>
                                    <td>
                                        {row.Fiscal_Status_Code
                                            ? <StatusBadge status={row.Fiscal_Status_Code} />
                                            : <Badge tone="slate">not reported</Badge>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const renderStockReport = () => {
        if (!data) return null;

        return (
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
                <StatCard
                    label="Total stock value"
                    value={`Rs. ${money(data.totalValue)}`}
                    sub={`${data.balance.length} items`}
                    tone="brand"
                />

                <div className="table-wrap">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Item</th>
                                <th>Category</th>
                                <th className="text-right">In stock</th>
                                <th className="text-right">Reorder at</th>
                                <th>State</th>
                                <th className="text-right">Avg cost</th>
                                <th className="text-right">Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.balance.map((row) => (
                                <tr key={row.Stock_Item_Code || row.Stock_Item_Name}>
                                    <td className="font-medium">{row.Stock_Item_Name}</td>
                                    <td className="text-slate-600">{row.Category_Name}</td>
                                    <td className="tabular text-right">
                                        {quantity(row.Current_Quantity)} {row.Stock_Unit}
                                    </td>
                                    <td className="tabular text-right text-slate-500">
                                        {quantity(row.Reorder_Level)}
                                    </td>
                                    <td><StatusBadge status={row.Stock_State} /></td>
                                    <td className="tabular text-right">{money(row.Average_Cost)}</td>
                                    <td className="tabular text-right font-medium">{money(row.Stock_Value)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const columns = COLUMNS[tab];
    const rows = tab === 'tax' || tab === 'stock' ? null : data;

    return (
        <div className="page">
            <PageHeader
                title="Reports"
                subtitle="Trading, tax and stock"
                actions={(
                    <>
                        <input
                            type="date"
                            className="input h-11 w-full min-w-0 sm:w-40"
                            value={range.fromDate}
                            onChange={(event) => setRange({ ...range, fromDate: event.target.value })}
                        />
                        <input
                            type="date"
                            className="input h-11 w-full min-w-0 sm:w-40"
                            value={range.toDate}
                            onChange={(event) => setRange({ ...range, toDate: event.target.value })}
                        />
                        <button
                            type="button"
                            className="btn-secondary"
                            disabled={!rows?.length && tab !== 'stock'}
                            onClick={() => {
                                if (tab === 'stock' && data?.balance) {
                                    exportCsv(
                                        data.balance,
                                        [
                                            { key: 'Stock_Item_Name', header: 'Item' },
                                            { key: 'Category_Name', header: 'Category' },
                                            { key: 'Current_Quantity', header: 'Quantity' },
                                            { key: 'Stock_Unit', header: 'Unit' },
                                            { key: 'Average_Cost', header: 'Average cost' },
                                            { key: 'Stock_Value', header: 'Value' }
                                        ],
                                        `stock-${range.toDate}.csv`
                                    );
                                } else if (tab === 'tax' && data?.invoices) {
                                    exportCsv(
                                        data.invoices,
                                        [
                                            { key: 'Order_Date_Time', header: 'Date', csv: (r) => formatDateTime(r.Order_Date_Time) },
                                            { key: 'Invoice_Number', header: 'Invoice' },
                                            { key: 'Authority_Invoice_Number', header: 'Authority number' },
                                            { key: 'Customer_Name', header: 'Customer' },
                                            { key: 'Customer_NTN', header: 'NTN' },
                                            { key: 'Total_Payable', header: 'Total' },
                                            { key: 'Tax_Amount', header: 'Sales tax' },
                                            { key: 'Fiscal_Status_Code', header: 'Reported' }
                                        ],
                                        `sales-tax-${range.fromDate}-to-${range.toDate}.csv`
                                    );
                                } else if (rows && columns) {
                                    exportCsv(rows, columns, `${tab}-${range.fromDate}-to-${range.toDate}.csv`);
                                }
                            }}
                        >
                            <FiDownload className="h-4 w-4" /> Export
                        </button>
                    </>
                )}
            />

            <div className="flex gap-2 overflow-x-auto scrollbar-none border-b border-slate-200">
                {TABS.map((item) => (
                    <button
                        key={item.key}
                        type="button"
                        onClick={() => setTab(item.key)}
                        className={`-mb-px flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2.5
                                    text-sm font-medium transition-colors sm:px-4 lg:py-2
                                    ${tab === item.key
                                        ? 'border-brand-600 text-brand-800'
                                        : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                    >
                        <item.icon className="h-4 w-4" /> {item.label}
                    </button>
                ))}
            </div>

            {tab === 'tax' ? renderTaxReport()
                : tab === 'stock' ? renderStockReport()
                    : (
                        <DataTable
                            className="min-h-0 flex-1"
                            columns={columns}
                            rows={rows || []}
                            loading={loading}
                            rowKey={(row, index) => index}
                            emptyTitle="Nothing in this range"
                            emptyDescription="Try a wider set of dates."
                            emptyIcon={FiFileText}
                        />
                    )}
        </div>
    );
};

export default ReportsPage;
